import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AiOutlineMenu } from 'react-icons/ai';
import { Sidebar, Cotizacion, DetalleCampana } from './components';
import { Dashboard, Ordenes, Stock, Clientes, Finanzas, Cotizaciones, Seguridad, Instalacion, Agenda, Login, Campanas } from './pages';
import './App.css';
import { useStateContext } from './contexts/ContextProvider';
import axios from 'axios';

const logoImage = `${process.env.PUBLIC_URL}/logo-filmsolar.svg`;
const avatarDefault = '/avatars/avatar.jpg';

// Mapea ruta → nombre de módulo (igual que en permissions_map.py del backend)
const ROUTE_TO_MODULE = {
  'dashboard':   'Dashboard',
  'cotizaciones':'Cotizaciones',
  'ordenes':     'Órdenes',
  'agenda':      'Agenda',
  'instalacion': 'Instalación',
  'stock':       'Stock',
  'clientes':    'Clientes',
  'finanzas':    'Finanzas',
  'seguridad':   'Seguridad',
  'marketing':   'Marketing',
};

// Mapea nombre de módulo → ruta (para redirigir al primer módulo disponible)
const MODULE_TO_ROUTE = {
  'Dashboard':   '/dashboard',
  'Cotizaciones':'/cotizaciones',
  'Órdenes':     '/ordenes',
  'Agenda':      '/agenda',
  'Instalación': '/instalacion',
  'Stock':       '/stock',
  'Clientes':    '/clientes',
  'Finanzas':    '/finanzas',
  'Seguridad':   '/seguridad',
  'Marketing':   '/marketing',
};

// Defined outside App so the component reference is stable across re-renders,
// preventing unnecessary unmount/remount of page components (e.g. Syncfusion grids).
const ProtectedRoute = ({ element, routeKey, userInfo }) => {
  if (!userInfo) return null;
  const modules = userInfo.modules || [];
  const required = ROUTE_TO_MODULE[routeKey];
  if (modules.includes(required)) return element;
  const firstRoute = modules.length > 0 ? (MODULE_TO_ROUTE[modules[0]] || '/login') : '/login';
  return <Navigate to={firstRoute} replace />;
};

const HomeRedirect = ({ userInfo }) => {
  if (!userInfo) return null;
  const modules = userInfo.modules || [];
  const firstRoute = modules.length > 0 ? (MODULE_TO_ROUTE[modules[0]] || '/login') : '/login';
  return <Navigate to={firstRoute} replace />;
};

const App = () => {
  const {
    activeMenu,
    currentColor,
    cotizacionVisible,
    setActiveMenu,
  } = useStateContext();

  const [userInfo, setUserInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserInfo(response.data);
        localStorage.setItem('username', response.data.username);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error al obtener la información del usuario:', error);
        localStorage.removeItem('access_token');
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };
    fetchUserInfo();
  }, []);

  const handleActiveMenu = () => setActiveMenu(!activeMenu);

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  return (
    <div>
      <BrowserRouter>
        {isAuthenticated ? (
          <div className="flex relative dark:bg-main-dark-bg overflow-x-hidden">

            <header
              className={`fixed top-0 w-full bg-[#256D9F] shadow-md z-50 grid grid-cols-[auto,1fr,auto] items-center px-4 py-2 transition-all duration-300 ${
                activeMenu ? 'md:ml-72' : 'ml-0'
              }`}
            >
              <button
                type="button"
                onClick={handleActiveMenu}
                style={{ color: currentColor }}
                className="text-2xl hover:bg-light-gray rounded-full w-10 h-10 flex items-center justify-center"
              >
                <AiOutlineMenu />
              </button>

              <div></div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <img
                    className="rounded-full w-8 h-8 border border-gray-200"
                    src={userInfo?.username ? `/avatars/${userInfo.username}.jpg` : avatarDefault}
                    onError={(e) => { e.target.src = avatarDefault; }}
                    alt="user-profile"
                  />
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-semibold text-white">
                      {userInfo?.username}
                    </span>
                    <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded">
                      {userInfo?.groups?.[0] || 'Usuario'}
                    </span>
                  </div>
                </div>

                <a href="/" className={activeMenu ? 'hidden' : 'flex items-center'}>
                  <img src={logoImage} alt="filmsolar" className="h-8 w-auto" />
                </a>
              </div>
            </header>

            {activeMenu ? (
              <div className={`fixed left-0 top-0 h-full dark:bg-secondary-dark-bg bg-[#256D9F] transition-all duration-300
                ${activeMenu ? "w-full md:w-64 lg:w-72 z-50" : "w-0 z-[-1]"}`}
                style={{ boxShadow: '4px 0 20px rgba(0,0,0,0.35)' }}
              >
                <Sidebar setIsAuthenticated={setIsAuthenticated} />
              </div>
            ) : (
              <div className="w-0 dark:bg-secondary-dark-bg">
                <Sidebar setIsAuthenticated={setIsAuthenticated} />
              </div>
            )}

            <div
              className={`transition-all duration-300 ${
                activeMenu ? 'md:ml-72' : 'ml-0'
              } dark:bg-main-dark-bg bg-gray-200 h-screen overflow-hidden flex flex-col w-full`}
              style={{ paddingTop: '64px' }}
            >
              {cotizacionVisible && <Cotizacion />}

              <div className="flex-1 overflow-hidden flex flex-col">
                <Routes>
                  <Route path="/" element={<HomeRedirect userInfo={userInfo} />} />
                  <Route path="/dashboard"    element={<ProtectedRoute routeKey="dashboard"    userInfo={userInfo} element={<Dashboard />} />} />
                  <Route path="/cotizaciones" element={<ProtectedRoute routeKey="cotizaciones" userInfo={userInfo} element={<Cotizaciones />} />} />
                  <Route path="/ordenes"      element={<ProtectedRoute routeKey="ordenes"      userInfo={userInfo} element={<Ordenes />} />} />
                  <Route path="/agenda"       element={<ProtectedRoute routeKey="agenda"       userInfo={userInfo} element={<Agenda />} />} />
                  <Route path="/instalacion"  element={<ProtectedRoute routeKey="instalacion"  userInfo={userInfo} element={<Instalacion />} />} />
                  <Route path="/stock"        element={<ProtectedRoute routeKey="stock"        userInfo={userInfo} element={<Stock />} />} />
                  <Route path="/clientes"     element={<ProtectedRoute routeKey="clientes"     userInfo={userInfo} element={<Clientes />} />} />
                  <Route path="/finanzas"     element={<ProtectedRoute routeKey="finanzas"     userInfo={userInfo} element={<Finanzas />} />} />
                  <Route path="/seguridad"    element={<ProtectedRoute routeKey="seguridad"    userInfo={userInfo} element={<Seguridad />} />} />
                  <Route path="/marketing"    element={<ProtectedRoute routeKey="marketing"    userInfo={userInfo} element={<Campanas />} />} />
                  <Route path="/marketing/:id" element={<ProtectedRoute routeKey="marketing"  userInfo={userInfo} element={<DetalleCampana />} />} />
                  <Route path="/login" element={<Navigate to="/" />} />
                </Routes>
              </div>
            </div>
          </div>
        ) : (
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        )}
      </BrowserRouter>
    </div>
  );
};

export default App;
